/**
 * HYPSEYA — Insights
 * Clinical analytics hub. All depth in one place.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Chart, Wave, Spark, ArrowRight } from '@/components/HypIcons';
import { HRVCard } from '@/hooks/useHealthKit';
import useHealthData from '@/hooks/useHealthData';
import Analytics from '@/analytics';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import orbUrl from '../orbAsset';
import { useSubscription } from '@/hooks/useSubscription';
import PaywallCard from '@/components/PaywallCard';
import MonthlyReportCard from '@/components/MonthlyReportCard';
import PracticeEffectivenessCard from '@/components/PracticeEffectivenessCard';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DARK = '#1E3A39';

// ── ERI Summary card ───────────────────────────────────────────────────────
function ERISummary({ userId }) {
  const [eri, setEri] = useState(null);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/training/eri/${userId}`)
      .then(r => setEri(r.data))
      .catch(() => {});
  }, [userId]);

  if (!eri) return null;

  const band = eri.eri_score >= 80 ? 'Strong' : eri.eri_score >= 60 ? 'Growing' : eri.eri_score >= 40 ? 'Emerging' : 'Early';
  const color = eri.eri_score >= 80 ? '#1E6B68' : eri.eri_score >= 60 ? '#2D8B87' : eri.eri_score >= 40 ? '#4AACA8' : '#B8906A';
  const pct = eri.eri_score || 0;

  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '10px' }}>
        <div>
          <div style={{ fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>
            Emotional Regulation Index
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: '700', color }}>{Math.round(pct)}</span>
            <span style={{ fontSize: '0.8rem', color: '#AAB8B6' }}>/100 · {band}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '0.78rem', color: '#8FA3A0' }}>
          {eri.total_sessions || 0} scored session{(eri.total_sessions || 0) !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height: 5, background: 'rgba(184,211,207,0.25)', borderRadius: 3, marginBottom: '14px' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: `linear-gradient(90deg, #1E6B68, ${color})`,
          borderRadius: 3, transition: 'width 0.8s ease'
        }} />
      </div>

      {/* 3 dimensions */}
      <div style={{ display: 'flex', gap: '8px' }}>
        {[
          { label: 'Regulation', value: eri.regulation_capacity },
          { label: 'Stability',  value: eri.stability_score },
          { label: 'Consistency', value: eri.practice_consistency }
        ].map(d => (
          <div key={d.label} style={{ flex: 1, textAlign: 'center', background: 'rgba(247,243,236,0.8)', borderRadius: '10px', padding: '8px 4px' }}>
            <div style={{ fontSize: '0.6rem', color: '#AAB8B6', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.06em', marginBottom: '2px' }}>{d.label}</div>
            <div style={{ fontSize: '0.95rem', fontWeight: '700', color, fontFamily: "'Playfair Display', serif" }}>{Math.round(d.value ?? 0)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ERI Population Benchmark ───────────────────────────────────────────────
function ERIBenchmark({ userId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/intelligence/eri-benchmark/${userId}`)
      .then(r => { if (r.data.has_data) setData(r.data); })
      .catch(() => {});
  }, [userId]);

  if (!data) return null;

  const TEAL = '#2D8B87', DARK = '#1E6B68';
  const pct  = data.percentile;
  // Colour shifts from amber (low) → teal (high)
  const barColor = pct >= 70 ? DARK : pct >= 40 ? TEAL : '#B8906A';

  return (
    <div style={{
      background: 'rgba(247,243,236,0.85)',
      border: '1px solid rgba(30,107,104,0.10)',
      borderRadius: '16px',
      padding: '16px 18px',
      marginTop: '14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#8FA3A0', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '3px' }}>
            Population Benchmark
          </div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: DARK, letterSpacing: '-0.5px' }}>
            Top {100 - pct + 1}%
          </div>
          <div style={{ fontSize: '11px', color: '#6B8B87', marginTop: '2px' }}>
            vs {data.population_size} {data.focus_area} practitioners
          </div>
        </div>
        {/* Percentile arc — simple SVG */}
        <svg width="64" height="64" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(184,211,207,0.3)" strokeWidth="6"/>
          <circle cx="32" cy="32" r="26" fill="none"
            stroke={barColor} strokeWidth="6"
            strokeDasharray={`${(pct / 100) * 163.4} 163.4`}
            strokeLinecap="round"
            transform="rotate(-90 32 32)"
            style={{ transition: 'stroke-dasharray 1.2s ease' }}
          />
          <text x="32" y="37" textAnchor="middle" fontSize="13" fontWeight="800" fill={DARK}>{pct}</text>
        </svg>
      </div>

      {/* Bar chart: user vs population */}
      <div style={{ marginBottom: '10px' }}>
        {[
          { label: 'You',      value: data.user_score,         color: DARK  },
          { label: 'Median',   value: data.population_median,  color: '#B8D3CF' },
          { label: 'Top 25%',  value: data.population_p75,     color: '#E0EAE8' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ marginBottom: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span style={{ fontSize: '11px', fontWeight: label === 'You' ? 700 : 400, color: '#4A6A66' }}>{label}</span>
              <span style={{ fontSize: '11px', fontWeight: 700, color: DARK }}>{Math.round(value)}</span>
            </div>
            <div style={{ height: '4px', background: 'rgba(0,0,0,0.06)', borderRadius: '2px' }}>
              <div style={{
                height: '100%', width: `${Math.min(100, (value / 100) * 100)}%`,
                background: color, borderRadius: '2px', transition: 'width 0.8s ease',
              }} />
            </div>
          </div>
        ))}
      </div>

      <p style={{ margin: 0, fontSize: '12px', color: '#6B8B87', lineHeight: 1.55 }}>
        {data.label}
        {data.user_trend_delta != null && (
          <span style={{ marginLeft: '6px', fontWeight: 600, color: data.user_trend_delta >= 0 ? TEAL : '#A33B2B' }}>
            {data.user_trend_delta >= 0 ? `↑ +${data.user_trend_delta}` : `↓ ${data.user_trend_delta}`} this fortnight.
          </span>
        )}
      </p>
    </div>
  );
}


// ── Tool card ──────────────────────────────────────────────────────────────
function ToolCard({ icon, title, desc, path, color, tag, locked = false, onLockedClick }) {
  const navigate = useNavigate();
  const handleClick = () => {
    if (locked) { onLockedClick?.(); return; }
    if (path) navigate(path);
  };
  return (
    <button
      onClick={handleClick}
      style={{
        width: '100%', textAlign: 'left',
        background: locked ? 'rgba(255,255,255,0.7)' : '#fff',
        borderRadius: '16px',
        padding: '18px 20px', marginBottom: '10px',
        border: `1px solid rgba(184,211,207,0.3)`,
        boxShadow: '0 2px 12px rgba(30,107,104,0.06)',
        cursor: locked ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', gap: '14px',
        transition: 'box-shadow 0.2s ease',
        opacity: locked ? 0.72 : 1,
      }}
      onMouseEnter={e => { if (!locked) e.currentTarget.style.boxShadow = '0 4px 20px rgba(30,107,104,0.12)'; }}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(30,107,104,0.06)'}
    >
      <div style={{
        width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
        background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1E3A39' }}>{title}</span>
          {tag && (
            <span style={{ fontSize: '0.62rem', background: `${color}18`, color, padding: '2px 8px', borderRadius: '10px', fontWeight: '600', letterSpacing: '0.04em' }}>
              {tag}
            </span>
          )}
          {locked && (
            <span style={{ fontSize: '0.62rem', background: 'rgba(201,152,62,0.12)', color: '#C9983E', padding: '2px 8px', borderRadius: '10px', fontWeight: '700', letterSpacing: '0.06em' }}>
              PRO
            </span>
          )}
        </div>
        <p style={{ margin: 0, fontSize: '0.78rem', color: '#8FA3A0', lineHeight: 1.4 }}>{desc}</p>
      </div>
      {locked ? (
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ opacity: 0.35 }}>
          <rect x="4" y="9" width="12" height="9" rx="2.5" stroke="#8FA3A0" strokeWidth="1.6"/>
          <path d="M7 9V6.5a3 3 0 0 1 6 0V9" stroke="#8FA3A0" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
      ) : <ArrowRight size={14} color="#B8D3CF" />}
    </button>
  );
}

// ── Share Report Card ──────────────────────────────────────────────────────
function ShareReportCard({ userId }) {
  const { getAccessTokenSilently } = useAuth0();
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expiry, setExpiry] = useState('');

  const generate = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const res = await axios.post(`${API}/share/report/${userId}`, {}, cfg);
      const url = `${window.location.origin}/therapist-report/${res.data.token}`;
      setLink(url);
      const exp = new Date(res.data.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
      setExpiry(exp);
    } catch {
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div style={{
      background: 'rgba(255,255,255,0.9)',
      borderRadius: 18,
      padding: '20px 22px',
      marginBottom: 12,
      border: '1px solid rgba(184,211,207,0.35)',
      boxShadow: '0 2px 16px rgba(30,107,104,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: 'rgba(139,107,174,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 2C10 2 4 5 4 11a6 6 0 0012 0c0-6-6-9-6-9z" stroke="#8B6BAE" strokeWidth="1.4" fill="none"/>
            <path d="M10 8v4M8 10h4" stroke="#8B6BAE" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1E3A39', marginBottom: 2 }}>
            Share with Therapist
          </div>
          <p style={{ margin: 0, fontSize: '0.78rem', color: '#8FA3A0', lineHeight: 1.4 }}>
            Generate a secure link to your clinical report. Valid 7 days, no account needed.
          </p>
        </div>
      </div>

      {!link ? (
        <button
          onClick={generate}
          disabled={loading}
          style={{
            width: '100%', padding: '12px',
            background: loading ? 'rgba(139,107,174,0.2)' : 'linear-gradient(135deg, #6B4E8B 0%, #8B6BAE 100%)',
            color: '#fff', border: 'none',
            borderRadius: 20, fontSize: '0.88rem',
            fontWeight: 600, cursor: loading ? 'default' : 'pointer',
            boxShadow: loading ? 'none' : '0 4px 16px rgba(139,107,174,0.3)',
            transition: 'all 0.2s',
          }}
        >
          {loading ? 'Generating report…' : 'Generate therapist link →'}
        </button>
      ) : (
        <div>
          <div style={{
            background: 'rgba(139,107,174,0.06)',
            border: '1px solid rgba(139,107,174,0.2)',
            borderRadius: 12, padding: '10px 14px',
            marginBottom: 10, wordBreak: 'break-all',
            fontSize: '0.75rem', color: '#6B4E8B', lineHeight: 1.5,
          }}>
            {link}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={copy}
              style={{
                flex: 1, padding: '11px',
                background: copied ? 'rgba(30,107,104,0.12)' : 'linear-gradient(135deg, #6B4E8B 0%, #8B6BAE 100%)',
                color: copied ? '#1E6B68' : '#fff',
                border: copied ? '1px solid rgba(30,107,104,0.25)' : 'none',
                borderRadius: 18, fontSize: '0.85rem',
                fontWeight: 600, cursor: 'pointer', transition: 'all 0.25s',
              }}
            >
              {copied ? '✓ Copied' : 'Copy link'}
            </button>
            <button
              onClick={() => { setLink(''); setExpiry(''); }}
              style={{
                padding: '11px 16px',
                background: 'transparent', color: '#AAB8B6',
                border: '1px solid rgba(184,211,207,0.5)',
                borderRadius: 18, fontSize: '0.82rem',
                cursor: 'pointer',
              }}
            >
              New
            </button>
          </div>
          {expiry && (
            <p style={{ margin: '8px 0 0', fontSize: '0.72rem', color: '#AABAB8', textAlign: 'center' }}>
              Expires {expiry} · no login required
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Weekly Summary Card ─────────────────────────────────────────────────────
function WeeklySummaryCard({ userId }) {
  const [summary, setSummary]       = useState(null);
  const [isLoading, setIsLoading]   = useState(false);
  const [isOpen, setIsOpen]         = useState(false);
  const [weeks, setWeeks]           = useState(1);
  const [autoTriggered, setAutoTriggered] = useState(false);

  // Load cached summary; auto-generate if none exists this week
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/training/weekly-summary/${userId}/latest`)
      .then(r => {
        if (r.data?.summary) {
          setSummary(r.data);
          // Auto-open if the summary is from this week
          const age = r.data.week_start
            ? (Date.now() - new Date(r.data.week_start)) / 86400000
            : 999;
          if (age < 8) setIsOpen(true);
        } else if (!autoTriggered) {
          // No cached summary — auto-generate silently
          setAutoTriggered(true);
          generate(true);
        }
      })
      .catch(() => {});
  }, [userId]); // eslint-disable-line

  const generate = async (silent = false) => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/training/weekly-summary/${userId}/generate?weeks=${weeks}`);
      if (res.data.generated) {
        setSummary(res.data);
        setIsOpen(true);
        if (!silent) toast.success('Weekly summary generated');
      } else if (!silent) {
        toast.error(res.data.message || 'Not enough data yet. Complete more sessions first.');
      }
    } catch {
      if (!silent) toast.error('Could not generate summary. Check your OpenAI credits.');
    } finally {
      setIsLoading(false);
    }
  };

  const trendIcon = (t) =>
    t === 'improving' ? '↑' : t === 'declining' ? '↓' : '→';
  const trendColor = (t) =>
    t === 'improving' ? '#1E6B68' : t === 'declining' ? '#C0392B' : '#8FA3A0';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)', borderRadius: 18,
      border: '1px solid rgba(184,211,207,0.3)',
      boxShadow: '0 2px 16px rgba(30,107,104,0.06)',
      marginBottom: 10, overflow: 'hidden',
    }}>
      {/* Header row */}
      <div style={{
        padding: '18px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: 'rgba(30,107,104,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 10h14M3 6h14M3 14h8" stroke="#1E6B68" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1E3A39', marginBottom: 2 }}>
              Weekly Summary
            </div>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#8FA3A0', lineHeight: 1.4 }}>
              AI-generated insight from your practice data.
            </p>
          </div>
        </div>
        {summary && (
          <button
            onClick={() => setIsOpen(o => !o)}
            style={{
              background: 'none', border: 'none', color: '#2D8B87',
              fontSize: '0.8rem', cursor: 'pointer', flexShrink: 0,
            }}
          >
            {isOpen ? 'Hide ↑' : 'View ↓'}
          </button>
        )}
      </div>

      {/* Auto-generating state */}
      {isLoading && autoTriggered && !summary && (
        <div style={{ padding: '4px 20px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {[0, 0.15, 0.3].map((d, i) => (
              <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: '#2D8B87', animation: `dotPulse 1.2s ease-in-out ${d}s infinite` }} />
            ))}
          </div>
          <style>{`@keyframes dotPulse { 0%,100%{opacity:.3;transform:scale(.8)} 50%{opacity:1;transform:scale(1)} }`}</style>
          <span style={{ fontSize: '0.8rem', color: '#8FA3A0' }}>Preparing your weekly analysis…</span>
        </div>
      )}

      {/* Period picker + generate */}
      {(!isLoading || summary) && (
      <div style={{ padding: '0 20px 18px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={weeks}
          onChange={e => setWeeks(Number(e.target.value))}
          style={{
            padding: '8px 12px', borderRadius: 10,
            border: '1.5px solid rgba(30,107,104,0.2)',
            fontSize: '0.84rem', background: 'rgba(247,243,236,0.9)',
            color: '#1E3A39', outline: 'none',
          }}
        >
          <option value={1}>Last 7 days</option>
          <option value={2}>Last 2 weeks</option>
          <option value={4}>Last month</option>
        </select>
        <button
          onClick={() => generate(false)}
          disabled={isLoading}
          style={{
            flex: 1, padding: '10px 18px',
            background: isLoading
              ? 'rgba(30,107,104,0.15)'
              : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
            color: isLoading ? '#2D8B87' : '#fff',
            border: 'none', borderRadius: 20,
            fontSize: '0.86rem', fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            boxShadow: isLoading ? 'none' : '0 3px 14px rgba(30,107,104,0.28)',
            transition: 'all 0.2s',
          }}
        >
          {isLoading ? 'Generating…' : summary ? '↺ Regenerate' : 'Generate summary →'}
        </button>
      </div>
      )}

      {/* Summary content */}
      {summary?.summary && isOpen && (
        <div style={{
          padding: '0 20px 24px',
          borderTop: '1px solid rgba(184,211,207,0.2)',
          paddingTop: 20,
        }}>
          {/* Stats strip */}
          {summary.stats && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8, marginBottom: 20,
            }}>
              {[
                { label: 'Sessions', value: summary.stats.total_sessions },
                {
                  label: 'Avg shift',
                  value: summary.stats.avg_delta != null
                    ? `${summary.stats.avg_delta > 0 ? '+' : ''}${summary.stats.avg_delta}`
                    : '—',
                },
                {
                  label: 'ERI',
                  value: summary.stats.eri != null
                    ? `${Math.round(summary.stats.eri)} ${trendIcon(summary.stats.eri_trend)}`
                    : '—',
                  color: trendColor(summary.stats.eri_trend),
                },
              ].map(s => (
                <div key={s.label} style={{
                  textAlign: 'center', padding: '10px 4px',
                  background: 'rgba(247,243,236,0.7)', borderRadius: 10,
                }}>
                  <div style={{ fontSize: '0.6rem', color: '#AAB8B6', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 3 }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: s.color || '#1E6B68', fontFamily: "'Playfair Display', serif" }}>
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Headline */}
          <p style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.05rem', color: '#1E3A39', lineHeight: 1.5,
            margin: '0 0 12px', fontStyle: 'italic',
          }}>
            "{summary.summary.headline}"
          </p>

          {/* Narrative */}
          <p style={{ color: '#666', fontSize: '0.88rem', lineHeight: 1.7, margin: '0 0 16px' }}>
            {summary.summary.narrative}
          </p>

          {/* Patterns */}
          {summary.summary.patterns?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '0.68rem', color: '#8FA3A0', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.08em', marginBottom: 8 }}>
                Patterns observed
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {summary.summary.patterns.map((p, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '8px 12px', background: 'rgba(30,107,104,0.04)',
                    borderRadius: 10, borderLeft: '2px solid #2D8B87',
                  }}>
                    <span style={{ color: '#2D8B87', fontSize: '0.82rem', lineHeight: 1.5 }}>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ERI insight */}
          {summary.summary.eri_insight && (
            <div style={{
              padding: '12px 14px', marginBottom: 14,
              background: 'rgba(184,211,207,0.12)', borderRadius: 12,
              border: '1px solid rgba(184,211,207,0.3)',
            }}>
              <div style={{ fontSize: '0.68rem', color: '#8FA3A0', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.08em', marginBottom: 4 }}>
                ERI
              </div>
              <p style={{ margin: 0, color: '#1E6B68', fontSize: '0.85rem', lineHeight: 1.55 }}>
                {summary.summary.eri_insight}
              </p>
            </div>
          )}

          {/* Recommendation */}
          {summary.summary.recommendation && (
            <div style={{
              padding: '12px 14px', marginBottom: 14,
              background: 'rgba(240,165,0,0.05)', borderRadius: 12,
              border: '1px solid rgba(240,165,0,0.18)',
            }}>
              <div style={{ fontSize: '0.68rem', color: '#B8860B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.08em', marginBottom: 4 }}>
                This week
              </div>
              <p style={{ margin: 0, color: '#7A5C1E', fontSize: '0.85rem', lineHeight: 1.55 }}>
                {summary.summary.recommendation}
              </p>
            </div>
          )}

          {/* Closing */}
          {summary.summary.closing && (
            <p style={{
              margin: 0, color: '#8FA3A0', fontSize: '0.82rem',
              lineHeight: 1.6, fontStyle: 'italic', textAlign: 'center',
            }}>
              {summary.summary.closing}
            </p>
          )}

          {/* Generated at */}
          {summary.week_start && (
            <p style={{ margin: '12px 0 0', color: '#CADBDA', fontSize: '0.7rem', textAlign: 'right' }}>
              Period starting {new Date(summary.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Assessment Card ───────────────────────────────────────────────────────
function AssessmentCard({ userId }) {
  const navigate = useNavigate();
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/assessments/${userId}/latest`)
      .then(r => setLatest(r.data && r.data.phq9_score !== undefined ? r.data : null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const isDue = () => {
    if (!latest) return true;
    const last = new Date(latest.created_at);
    const diff = (Date.now() - last.getTime()) / (1000 * 60 * 60 * 24);
    return diff >= 30;
  };

  const phqSev = latest ? (() => {
    const s = latest.phq9_score;
    if (s <= 4) return { label: 'Minimal', color: '#4AACA8' };
    if (s <= 9) return { label: 'Mild', color: '#B8906A' };
    if (s <= 14) return { label: 'Moderate', color: '#E07B4A' };
    return { label: 'Severe', color: '#C0392B' };
  })() : null;

  const gadSev = latest ? (() => {
    const s = latest.gad7_score;
    if (s <= 4) return { label: 'Minimal', color: '#4AACA8' };
    if (s <= 9) return { label: 'Mild', color: '#B8906A' };
    if (s <= 14) return { label: 'Moderate', color: '#E07B4A' };
    return { label: 'Severe', color: '#C0392B' };
  })() : null;

  if (loading) return null;

  if (!latest) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(30,107,104,0.08) 0%, rgba(184,211,207,0.15) 100%)',
        border: '1.5px dashed rgba(30,107,104,0.3)',
        borderRadius: '16px', padding: '20px', marginBottom: '10px',
        display: 'flex', alignItems: 'center', gap: '16px',
      }}>
        <div style={{ width: 36, height: 36, background: 'rgba(30,107,104,0.1)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Chart size={20} color="#1E6B68" />
          </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#1E6B68', fontSize: '0.9rem' }}>Baseline assessment pending</p>
          <p style={{ margin: 0, color: '#666', fontSize: '0.8rem' }}>PHQ-9 + GAD-7 · Takes 3 minutes</p>
        </div>
        <button
          onClick={() => navigate('/assessment?returnTo=/insights')}
          style={{ padding: '8px 16px', background: '#1E6B68', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          Start →
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', borderRadius: '16px', padding: '18px 20px', marginBottom: '10px', boxShadow: '0 2px 12px rgba(30,107,104,0.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <p style={{ margin: '0 0 2px', fontWeight: '700', color: '#1E3A3A', fontSize: '0.9rem' }}>Clinical Assessment</p>
          <p style={{ margin: 0, color: '#888', fontSize: '0.75rem' }}>
            {new Date(latest.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>
        {isDue() && (
          <button
            onClick={() => navigate('/assessment?returnTo=/insights&followup=1')}
            style={{ padding: '6px 14px', background: 'rgba(30,107,104,0.1)', color: '#1E6B68', border: 'none', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
          >
            Retake
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        {[{ label: 'PHQ-9', score: latest.phq9_score, max: 27, sev: phqSev }, { label: 'GAD-7', score: latest.gad7_score, max: 21, sev: gadSev }].map(item => (
          <div key={item.label} style={{ background: '#F8F8F8', borderRadius: '10px', padding: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '0.72rem', color: '#888', fontWeight: '600' }}>{item.label}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: '700', color: item.sev.color }}>{item.score}<span style={{ fontSize: '0.65rem', color: '#aaa' }}>/{item.max}</span></span>
            </div>
            <div style={{ height: '5px', background: '#E8E8E8', borderRadius: '3px' }}>
              <div style={{ height: '100%', width: `${(item.score / item.max) * 100}%`, background: item.sev.color, borderRadius: '3px' }} />
            </div>
            <span style={{ fontSize: '0.7rem', color: item.sev.color, fontWeight: '600', marginTop: '4px', display: 'block' }}>{item.sev.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
// ── Biometric Signal Card ─────────────────────────────────────────────────────
function BiometricSignalCard({ userId }) {
  const { healthData, loading } = useHealthData(userId);
  const isNative = typeof window !== 'undefined' &&
    Boolean(window.Capacitor?.isNativePlatform?.());

  // Only renders on native — never shows the promo on web
  if (!isNative || loading || !healthData) return null;

  const { hrv_ms, resting_hr_bpm, hrv_score, rhr_score, sleep_score, biometric_score, source } = healthData;
  const hasData = hrv_ms || resting_hr_bpm || sleep_score !== null;
  if (!hasData) return null;

  const scoreColor = biometric_score >= 70 ? '#2D8B87'
    : biometric_score >= 45 ? '#4AACA8'
    : '#B8906A';

  const hrvLabel = hrv_score >= 70 ? 'Good variability'
    : hrv_score >= 45 ? 'Moderate variability'
    : hrv_score !== null ? 'Low variability — rest is indicated'
    : null;

  const platformLabel = source === 'healthkit' ? 'Apple Health' : 'Health Connect';

  return (
    <div style={{
      background: '#fff', borderRadius: '18px', padding: '18px 20px',
      marginBottom: '16px', boxShadow: '0 2px 16px rgba(30,107,104,0.07)',
      border: '1px solid rgba(184,211,207,0.3)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ fontSize: '0.65rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600' }}>
          {platformLabel} · Biometric Signal
        </div>
        {biometric_score !== null && (
          <div style={{
            background: `${scoreColor}15`, borderRadius: '20px',
            padding: '4px 12px', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: scoreColor }} />
            <span style={{ fontSize: '0.75rem', fontWeight: '700', color: scoreColor }}>
              {biometric_score}/100
            </span>
          </div>
        )}
      </div>

      {/* Metrics */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: hrvLabel ? '12px' : 0 }}>
        {hrv_ms && (
          <div style={{ flex: 1, background: 'rgba(247,243,236,0.8)', borderRadius: '12px', padding: '10px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#AAB8B6', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.08em', marginBottom: '3px' }}>HRV</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1E3A39', fontFamily: "'Playfair Display', serif" }}>
              {hrv_ms}<span style={{ fontSize: '0.65rem', color: '#8FA3A0' }}>ms</span>
            </div>
          </div>
        )}
        {resting_hr_bpm && (
          <div style={{ flex: 1, background: 'rgba(247,243,236,0.8)', borderRadius: '12px', padding: '10px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#AAB8B6', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.08em', marginBottom: '3px' }}>Resting HR</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1E3A39', fontFamily: "'Playfair Display', serif" }}>
              {resting_hr_bpm}<span style={{ fontSize: '0.65rem', color: '#8FA3A0' }}>bpm</span>
            </div>
          </div>
        )}
        {sleep_score !== null && (
          <div style={{ flex: 1, background: 'rgba(247,243,236,0.8)', borderRadius: '12px', padding: '10px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#AAB8B6', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.08em', marginBottom: '3px' }}>Sleep Quality</div>
            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1E3A39', fontFamily: "'Playfair Display', serif" }}>
              {sleep_score}<span style={{ fontSize: '0.65rem', color: '#8FA3A0' }}>%</span>
            </div>
          </div>
        )}
      </div>

      {hrvLabel && (
        <div style={{
          padding: '9px 12px', borderRadius: '10px',
          background: `${scoreColor}10`, border: `1px solid ${scoreColor}25`,
          fontSize: '0.78rem', color: scoreColor, fontWeight: '500'
        }}>
          {hrvLabel}
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function Insights() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth0();
  const [localUser, setLocalUser] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { try { setLocalUser(JSON.parse(u)); } catch {} }
    setTimeout(() => setIsVisible(true), 60);
    Analytics.insightsViewed();
  }, []);

  const userId = authUser?.sub || localUser?.user_id;
  const subscription = useSubscription(userId);
  const isPro = subscription?.isPro ?? false;

  const SectionLabel = ({ label, top = 24 }) => (
    <div style={{
      fontSize: '0.62rem', color: '#AAB8B6', letterSpacing: '0.14em',
      textTransform: 'uppercase', fontWeight: '700',
      marginBottom: '10px', marginTop: top,
    }}>
      {label}
    </div>
  );

  const isClinical = ['therapist', 'doctor', 'institution'].includes(localUser?.role);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 55%, rgba(184,211,207,0.15) 100%)',
      padding: '28px 20px 100px', maxWidth: '520px', margin: '0 auto',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
      transition: 'opacity 0.45s ease, transform 0.45s ease',
    }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: '24px', paddingTop: '8px' }}>
        <h1 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.7rem', color: '#1E3A39',
          margin: '0 0 4px', fontWeight: '500',
        }}>Insights</h1>
        <p style={{ margin: '0 0 6px', color: '#5C7470', fontSize: '0.82rem', fontWeight: 500 }}>
          Your nervous system, measured.
        </p>
        <p style={{ margin: 0, color: '#AAB8B6', fontSize: '0.74rem', lineHeight: 1.5 }}>
          Everything here is built from your own sessions. The more you practise, the sharper it gets — and the more it becomes yours alone.
        </p>
      </div>

      {/* ━━━ 1. ERI ━━━ */}
      <div style={{
        background: '#fff', borderRadius: '20px',
        padding: '22px 20px',
        boxShadow: '0 2px 20px rgba(30,107,104,0.07)',
        borderLeft: '3px solid #1E6B68',
        marginBottom: '8px',
      }}>
        {userId
          ? <><ERISummary userId={userId} /><ERIBenchmark userId={userId} /></>
          : <p style={{ color: '#AAB8B6', fontSize: '0.85rem', margin: 0 }}>Complete a session to see your ERI.</p>
        }
      </div>

      {/* Inline share action — only when there's data */}
      {userId && isPro && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
          <ShareReportCard userId={userId} compact />
        </div>
      )}

      {/* ━━━ WHAT REGULATES YOU — adaptive n-of-1 engine ━━━ */}
      <SectionLabel label="What regulates you" />
      {userId && <PracticeEffectivenessCard userId={userId} />}

      {/* ━━━ ZONE 2 · YOUR STORY — one home for the AI narrative & patterns ━━━ */}
      <SectionLabel label="Your story" />
      {userId && <MonthlyReportCard userId={userId} />}

      <div style={{ marginTop: 8 }}>
        {userId && (
          isPro
            ? <WeeklySummaryCard userId={userId} />
            : <PaywallCard
                compact
                feature="Weekly AI Summary"
                reason="AI-generated patterns, ERI trends, and personalised recommendations — every week."
                onUpgrade={subscription.startCheckout}
              />
        )}
      </div>

      {/* Patterns — AI longitudinal analysis (moved here from "Go deeper") */}
      {userId && (
        <button
          onClick={() => isPro ? navigate('/pattern-intelligence') : subscription.startCheckout(process.env.REACT_APP_STRIPE_PRICE_YEARLY || '')}
          style={{
            width: '100%', marginTop: 8,
            padding: '14px 16px', background: '#fff',
            border: '1px solid rgba(30,107,104,0.12)',
            borderRadius: 16, cursor: 'pointer', textAlign: 'left',
            boxShadow: '0 1px 8px rgba(30,107,104,0.05)',
            opacity: isPro ? 1 : 0.85,
            display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <Spark size={18} color="#1E6B68" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.84rem', fontWeight: '700', color: DARK }}>Patterns</div>
            <div style={{ fontSize: '0.72rem', color: '#8FA3A0', lineHeight: 1.4 }}>AI analysis of your regulation tendencies</div>
          </div>
          {!isPro && <span style={{ fontSize: '0.62rem', color: '#B8906A', fontWeight: 700, letterSpacing: '0.06em' }}>PRO</span>}
          <span style={{ color: '#B8D3CF' }}>→</span>
        </button>
      )}

      {userId && window.Capacitor?.isNativePlatform?.() && (
        <div style={{ marginTop: 8 }}>
          <BiometricSignalCard userId={userId} />
        </div>
      )}

      {/* ━━━ ZONE 3 · YOUR CLINICAL PICTURE ━━━ */}
      <SectionLabel label="Your clinical picture" />

      {/* Clinical Assessment */}
      {userId && <AssessmentCard userId={userId} />}

      {/* Proof of Change + Clinical Report — compact row */}
      <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 2 }}>
        <button
          onClick={() => navigate('/proof')}
          style={{
            flex: 1, padding: '14px 16px',
            background: 'linear-gradient(135deg, rgba(30,58,56,0.92) 0%, rgba(45,107,104,0.92) 100%)',
            border: 'none', borderRadius: 16, cursor: 'pointer', textAlign: 'left',
            boxShadow: '0 2px 14px rgba(30,107,104,0.18)',
          }}
        >
          <div style={{ fontSize: '0.58rem', fontWeight: '700', color: 'rgba(255,255,255,0.38)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Evolution</div>
          <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#fff', fontFamily: "'Playfair Display', serif" }}>Proof of Change</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginTop: 3 }}>ERI · PHQ-9 · GAD-7</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, marginTop: 10 }}>View →</div>
        </button>

        <button
          onClick={() => isPro ? navigate('/clinical-report') : subscription.startCheckout(process.env.REACT_APP_STRIPE_PRICE_YEARLY || '')}
          style={{
            flex: 1, padding: '14px 16px',
            background: 'rgba(255,255,255,0.75)',
            border: '1px solid rgba(30,107,104,0.14)',
            borderLeft: '3px solid #4AACA8',
            borderRadius: 16, cursor: 'pointer', textAlign: 'left',
            opacity: isPro ? 1 : 0.85,
          }}
        >
          <div style={{ fontSize: '0.58rem', fontWeight: '700', color: '#4AACA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Export</div>
          <div style={{ fontSize: '0.9rem', fontWeight: '600', color: DARK, fontFamily: "'Playfair Display', serif" }}>Clinical Report</div>
          <div style={{ fontSize: '0.72rem', color: '#8FA3A0', marginTop: 3, lineHeight: 1.4 }}>Shareable with your therapist</div>
          <div style={{ fontSize: '0.72rem', color: '#2D8B87', fontWeight: 600, marginTop: 10 }}>{isPro ? 'Open →' : 'PRO →'}</div>
        </button>
      </div>

      {/* ━━━ ZONE 4 · EXPLORE ━━━ */}
      <SectionLabel label="Explore" />

      {/* 2×2 compact grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>

        {/* Somatic Map */}
        <button
          onClick={() => navigate('/somatic-map')}
          style={{
            padding: '14px 14px', background: '#fff',
            border: '1px solid rgba(30,107,104,0.12)',
            borderRadius: 16, cursor: 'pointer', textAlign: 'left',
            boxShadow: '0 1px 8px rgba(30,107,104,0.05)',
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <ellipse cx="10" cy="5" rx="3" ry="3" stroke="#4AACA8" strokeWidth="1.4"/>
              <path d="M7 8h6l1 8H6l1-8z" stroke="#4AACA8" strokeWidth="1.4" strokeLinejoin="round"/>
              <path d="M6 11l-2 5M14 11l2 5" stroke="#4AACA8" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </div>
          <div style={{ fontSize: '0.84rem', fontWeight: '700', color: DARK, marginBottom: 2 }}>Somatic Map</div>
          <div style={{ fontSize: '0.7rem', color: '#8FA3A0', lineHeight: 1.4 }}>Where in your body emotion lives</div>
        </button>

        {/* Window of Tolerance */}
        <button
          onClick={() => navigate('/window-of-tolerance')}
          style={{
            padding: '14px 14px', background: '#fff',
            border: '1px solid rgba(30,107,104,0.12)',
            borderRadius: 16, cursor: 'pointer', textAlign: 'left',
            boxShadow: '0 1px 8px rgba(30,107,104,0.05)',
          }}
        >
          <div style={{ marginBottom: 8 }}><Wave size={18} color="#2D8B87" /></div>
          <div style={{ fontSize: '0.84rem', fontWeight: '700', color: DARK, marginBottom: 2 }}>Tolerance Window</div>
          <div style={{ fontSize: '0.7rem', color: '#8FA3A0', lineHeight: 1.4 }}>Hyper, regulated, or hypo</div>
        </button>

        {/* Emotional Timeline */}
        <button
          onClick={() => navigate('/emotional-timeline')}
          style={{
            padding: '14px 14px', background: '#fff',
            border: '1px solid rgba(30,107,104,0.12)',
            borderRadius: 16, cursor: 'pointer', textAlign: 'left',
            boxShadow: '0 1px 8px rgba(30,107,104,0.05)',
          }}
        >
          <div style={{ marginBottom: 8 }}><Spark size={18} color="#2D8B87" /></div>
          <div style={{ fontSize: '0.84rem', fontWeight: '700', color: DARK, marginBottom: 2 }}>Emotional Timeline</div>
          <div style={{ fontSize: '0.7rem', color: '#8FA3A0', lineHeight: 1.4 }}>Your journey, day by day</div>
        </button>

      </div>

      {/* ━━━ Clinical Portal (practitioners only) ━━━ */}
      {isClinical && (
        <>
          <SectionLabel label="Clinical portal" />
          <div
            onClick={() => navigate('/therapist-dashboard')}
            style={{
              background: 'linear-gradient(135deg, #1A3550 0%, #2D6B87 100%)',
              borderRadius: '18px', padding: '18px 20px',
              marginBottom: '10px', cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(45,107,135,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <div>
              <p style={{ margin: '0 0 3px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>For practitioners</p>
              <h3 style={{ margin: '0 0 4px', fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1rem', fontWeight: '400' }}>Therapist Portal</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' }}>Patients · ERI · PHQ-9 · Alerts</p>
            </div>
            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', fontWeight: '600', flexShrink: 0, marginLeft: 12 }}>Open →</span>
          </div>
        </>
      )}

      {/* ━━━ Safety disclaimer ━━━ */}
      <div style={{
        marginTop: '32px', padding: '14px 18px',
        background: 'rgba(255,255,255,0.5)', borderRadius: '12px',
        border: '1px solid rgba(30,107,104,0.08)', textAlign: 'center',
      }}>
        <p style={{ margin: 0, fontSize: '0.7rem', color: '#AAB8B6', lineHeight: 1.6 }}>
          HYPSEYA is a wellness tool — not a substitute for professional mental health support.
          In crisis: <strong style={{ color: '#1E6B68' }}>116 123</strong> (EU) · <strong style={{ color: '#1E6B68' }}>112</strong> (Emergency).
        </p>
      </div>

    </div>
  );
}
